import React, { useState } from 'react';
import { AlertTriangleIcon, CheckIcon, CrosshairIcon, ListIcon } from 'lucide-react';
import { Toggle } from '../ui/Toggle';
type ValidationSidebarProps = {
  supportsGenerated: boolean;
};
function PassBadge() {
  return <span className="flex h-[11px] w-[11px] items-center justify-center rounded-full bg-[#2bb34a] text-white" aria-label="Passed" data-id="element-95" data-mp="3vmw78r52q2p0">
      <CheckIcon className="h-[8px] w-[8px]" strokeWidth={3.5} data-id="element-96" data-mp="3mnsmzju67h8x" />
    </span>;
}
export function ValidationSidebar({
  supportsGenerated
}: ValidationSidebarProps) {
  const [open, setOpen] = useState(true);
  const [checks, setChecks] = useState({
    undersupported: true,
    minima: true,
    cups: true,
    autoRelease: false
  });
  const set = (k: keyof typeof checks) => (v: boolean) => setChecks(p => ({
    ...p,
    [k]: v
  }));
  const rows: {
    key: keyof typeof checks;
    label: string;
    status: React.ReactNode;
  }[] = [{
    key: 'undersupported',
    label: 'Undersupported Models',
    status: supportsGenerated ? <PassBadge data-id="element-97" data-mp="15x29khfzpa2x" /> : <span className="text-[12px] text-pf-ink" data-id="element-98" data-mp="3zh8v79sq7cp">1</span>
  }, {
    key: 'minima',
    label: 'Minima',
    status: <PassBadge data-id="element-99" data-mp="41two128jbxo" />
  }, {
    key: 'cups',
    label: 'Cups',
    status: <PassBadge data-id="element-100" data-mp="8zbyl7hu4qw6" />
  }, {
    key: 'autoRelease',
    label: 'Form Auto Release',
    status: null
  }];
  return <aside aria-label="Print settings and validation" className="flex w-[232px] shrink-0 flex-col border-l border-pf-line bg-white" data-id="element-101" data-mp="3q7z78kcpow8i">
      <div className="p-[12px] pb-[13px]" data-id="element-102" data-mp="3v7lmezc3omjy">
        <div className="rounded-[5px] border border-[#e3e5e8] bg-pf-panel" data-id="element-103" data-mp="mpp9niq0wlpq">
          <div className="px-[12px] pb-[9px] pt-[7px]" data-id="element-104" data-mp="rosd4yqcujvl">
            <h2 className="text-[11px] font-bold text-pf-ink" data-id="element-105" data-mp="roa75adw4rah">Printer</h2>
            <button type="button" className="mt-[2px] flex w-full items-start gap-[12px] text-left" data-id="element-106" data-mp="cze2zoeyfyf5">
              <svg viewBox="0 0 20 24" className="mt-[2px] h-[24px] w-[18px] text-[#9da2a8]" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true" data-id="element-107" data-mp="hxxq3s5abg93">
                <rect x="1" y="1" width="18" height="22" rx="2.5" data-id="element-108" data-mp="1ag31p98lykkb" />
                <path d="M1 11 H19 M5 16 H15" data-id="element-109" data-mp="1ahmf8s2vqhie" />
              </svg>
              <span data-id="element-110" data-mp="1ka8oshfws8nh">
                <span className="flex items-center gap-[5px] text-[12.5px] font-bold text-pf-ink" data-id="element-111" data-mp="1k9fnvx3msdd9">
                  <AlertTriangleIcon className="h-[11px] w-[11px] fill-[#f5a623] text-white" strokeWidth={2.5} data-id="element-112" data-mp="1facho9bx013p" />
                  No Printer Selected
                </span>
                <span className="block text-[11px] text-pf-muted" data-id="element-113" data-mp="16az71dc216he">Form 3</span>
              </span>
            </button>
          </div>
          <div className="border-t border-[#e3e5e8] px-[12px] pb-[8px] pt-[7px]" data-id="element-114" data-mp="11eiiii0y0q3s">
            <h2 className="pb-[2px] text-[11px] font-bold text-pf-ink" data-id="element-115" data-mp="wkmpltzjdtfh">Job Settings</h2>
            {[['Material', 'Alumina 4N V1'], ['Layer Thickness', '0.050 mm'], ['Print Settings', 'Default']].map(([k, v]) => <div key={k} className="flex h-[17px] items-center justify-between text-[11px]" data-id="element-116" data-mp="img0p3ckf3ry">
                <span className="text-pf-muted" data-id="element-117" data-mp="dmtliod6fdgi">{k}</span>
                <span className="text-pf-ink" data-id="element-118" data-mp="dpfsomw5nqy7">{v}</span>
              </div>)}
          </div>
        </div>
      </div>

      <div className="border-t border-pf-line" data-id="element-119" data-mp="2u908fidsoaf8">
        <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)} className="flex h-[28px] w-full items-center justify-between px-[12px]" data-id="element-120" data-mp="2u7ouwqujroax">
          <h2 className="text-[13px] font-bold text-pf-ink" data-id="element-121" data-mp="33aho4znaia3e">Print Validation</h2>
          <svg viewBox="0 0 8 5" className={`h-[4px] w-[7px] text-pf-ink transition-transform duration-150 ${open ? '' : '-rotate-90'}`} fill="currentColor" aria-hidden="true" data-id="element-122" data-mp="26k7ajgdm3shz">
            <path d="M0 0 H8 L4 5 Z" data-id="element-123" data-mp="2bgnvy2tffsrd" />
          </svg>
        </button>
        {open && <div className="px-[12px] pb-[5px]" data-id="element-124" data-mp="2fiiwh53ja243">
            {rows.map(r => <div key={r.key} className="flex h-[20px] items-center justify-between" data-id="element-125" data-mp="2kc59iydm080f">
                <span className="text-[11px] text-pf-muted" data-id="element-126" data-mp="1nsed99rifl4g">{r.label}</span>
                <span className="flex items-center gap-[12px]" data-id="element-127" data-mp="1ruj5q5682bpi">
                  {r.status}
                  <Toggle label={r.label} checked={checks[r.key]} onChange={set(r.key)} data-id="element-128" data-mp="18pwje9vgouw1" />
                </span>
              </div>)}
          </div>}
        {!supportsGenerated && checks.undersupported && <button type="button" className="flex h-[26px] w-full items-center gap-[8px] border-y border-pf-line border-l-[3px] border-l-[#e02424] pl-[10px] pr-[12px] text-left hover:bg-pf-panel" data-id="element-129" data-mp="1ihaoqya3gox3">
            <svg viewBox="0 0 12 12" className="h-[11px] w-[11px]" aria-hidden="true" data-id="element-130" data-mp="1rg4f46wu0j27">
              <path d="M6 0.5 L11.5 6 L6 11.5 L0.5 6 Z" fill="#e02424" data-id="element-131" data-mp="1michqxdf44qm" />
              <path d="M6 3 V6.6" stroke="white" strokeWidth="1.4" strokeLinecap="round" data-id="element-132" data-mp="21f3fz3bcvaen" />
              <circle cx="6" cy="8.6" r="0.8" fill="white" data-id="element-133" data-mp="21cqzyvp5ra48" />
            </svg>
            <span className="flex-1 text-[11.5px] text-pf-ink" data-id="element-134" data-mp="1wgs5qrh5exvw">Models Need More Support</span>
            <CrosshairIcon className="h-[11px] w-[11px] text-[#4b5056]" strokeWidth={2} data-id="element-135" data-mp="71lz1z5ghbsl" />
            <svg viewBox="0 0 5 8" className="ml-[6px] h-[7px] w-[5px] text-pf-ink" fill="currentColor" aria-hidden="true" data-id="element-136" data-mp="23aotnb90z9u">
              <path d="M0 0 L5 4 L0 8 Z" data-id="element-137" data-mp="2drflncg0wwnl" />
            </svg>
          </button>}
      </div>

      <div className="mt-auto p-[12px]" data-id="element-138" data-mp="2iqife0qjzgg9">
        <button type="button" disabled={!supportsGenerated} className="flex h-[24px] w-full items-center justify-center gap-[8px] rounded-full bg-pf-blue text-[12px] text-white transition-colors duration-150 hover:bg-pf-blue-hover disabled:bg-[#e3e4e6] disabled:text-[#b0b4b9]" data-id="element-139" data-mp="2401hush1haon">
          
          <ListIcon className="h-[12px] w-[12px]" strokeWidth={2} data-id="element-140" data-mp="1z1hvws2g3ywa" />
          Upload to Queue
        </button>
      </div>
    </aside>;
}